import { FileWithUploadMetadata } from '@/lib/type'
import { getImageMetadata, uploadFileToService } from '@/lib/utils/fileUpload'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

/**
 * Generate a unique key for a file based on its metadata
 * Used consistently across the hook for file identification
 */
const getFileKey = (file: File): string => {
  return `${file.name}-${file.size}-${file.lastModified}`
}

/**
 * Check if two files match based on their key properties
 */
const filesMatch = (file1: File, file2: File): boolean => {
  return (
    file1.name === file2.name &&
    file1.size === file2.size &&
    file1.lastModified === file2.lastModified
  )
}

/**
 * Shared hook for file upload functionality with progress tracking
 * Used by landing page components (AiBox, LandingInput) and chat components
 */
export function useFileUpload() {
  const [files, setFiles] = useState<FileWithUploadMetadata[]>([])
  const [fileObjectUrls, setFileObjectUrls] = useState<Map<string, string>>(new Map())
  const fileObjectUrlsRef = useRef<Map<string, string>>(fileObjectUrls)

  /**
   * Add files to the upload queue and start uploading immediately
   */
  const addFiles = useCallback((newFiles: File[]) => {
    // Mark files as uploading
    const filesWithUploadState = newFiles.map((file) => {
      const fileWithMeta = file as FileWithUploadMetadata
      fileWithMeta.isUploading = true
      fileWithMeta.uploadProgress = 0
      return fileWithMeta
    })

    setFiles((currentFiles) => {
      const updatedFiles = [...currentFiles, ...filesWithUploadState]

      // Create object URLs for new files
      setFileObjectUrls((currentUrls) => {
        const newUrls = new Map(currentUrls)
        filesWithUploadState.forEach((file) => {
          const key = getFileKey(file)
          if (!newUrls.has(key)) {
            newUrls.set(key, URL.createObjectURL(file))
          }
        })
        return newUrls
      })

      return updatedFiles
    })

    // Upload files immediately with progress tracking
    filesWithUploadState.forEach(async (file) => {
      const key = getFileKey(file)

      try {
        const result = await uploadFileToService(file, (progress) => {
          setFiles((currentFiles) =>
            currentFiles.map((f) => {
              if (filesMatch(f, file)) {
                const updated = f as FileWithUploadMetadata
                updated.uploadProgress = progress
                return updated
              }
              return f
            })
          )
        })

        if (!result.success || !result.url) {
          throw new Error(result.error || 'File upload failed')
        }

        // Check if file still exists before updating (prevents race condition)
        setFiles((currentFiles) => {
          const fileStillExists = currentFiles.some((f) => filesMatch(f, file))
          if (!fileStillExists) {
            // File was removed while upload was in progress, don't update
            return currentFiles
          }

          return currentFiles.map((f) => {
            if (filesMatch(f, file)) {
              const updated = f as FileWithUploadMetadata
              updated.isUploading = false
              updated.uploadProgress = 100
              updated.uploadedUrl = result.url
              return updated
            }
            return f
          })
        })

        // Extract image metadata for images
        if (file.type.startsWith('image/')) {
          getImageMetadata(file).then((metadata) => {
            if (metadata) {
              setFiles((currentFiles) =>
                currentFiles.map((f) => {
                  if (filesMatch(f, file)) {
                    const withMeta = f as FileWithUploadMetadata
                    withMeta.imageMetadata = metadata
                    return withMeta
                  }
                  return f
                })
              )
            }
          })
        }
      } catch (error) {
        console.error('File upload failed:', error)
        // Remove file on upload failure and clean up object URL
        setFiles((currentFiles) => currentFiles.filter((f) => !filesMatch(f, file)))
        // Revoke object URL to prevent memory leak
        setFileObjectUrls((currentUrls) => {
          const newUrls = new Map(currentUrls)
          const url = newUrls.get(key)
          if (url) {
            URL.revokeObjectURL(url)
            newUrls.delete(key)
          }
          return newUrls
        })

        // Show the actual error message from the backend/upload service
        const errorMessage = error instanceof Error ? error.message : 'Failed to upload file'
        toast.error(errorMessage)
      }
    })
  }, [])

  /**
   * Remove a file from the upload queue and clean up its object URL
   * Uses key-based identification (name, size, lastModified) for consistency
   */
  const removeFile = useCallback((fileToRemove: File) => {
    const key = getFileKey(fileToRemove)

    // Remove file by matching key properties, not reference equality
    setFiles((currentFiles) => currentFiles.filter((f) => !filesMatch(f, fileToRemove)))

    // Clean up object URL
    setFileObjectUrls((currentUrls) => {
      const newUrls = new Map(currentUrls)
      const url = newUrls.get(key)
      if (url) {
        URL.revokeObjectURL(url)
        newUrls.delete(key)
      }
      return newUrls
    })
  }, [])

  /**
   * Check if any files are currently uploading or downloading
   */
  const hasUploadingFiles = files.some(
    (file) => file.isDownloading === true || file.isUploading === true
  )

  // Keep ref in sync with state
  useEffect(() => {
    fileObjectUrlsRef.current = fileObjectUrls
  }, [fileObjectUrls])

  // Cleanup all object URLs on unmount only
  useEffect(() => {
    return () => {
      // Only runs on unmount, revokes all URLs from the latest Map
      fileObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [])

  return {
    files,
    fileObjectUrls,
    addFiles,
    removeFile,
    hasUploadingFiles,
    setFiles,
    setFileObjectUrls,
  }
}
